import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, retry, tap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { ContaContabil } from '../models/contaContabil.model';
import { TipoBanco } from '../models/enums/tipoBanco.enum';
import { Dre } from '../models/dre.model';
import { Balancete } from '../models/balancete.model';
import { TransacaoContabil } from '../models/transacaoContabil.model';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class PlanoDeContasService {
  private apiUrl = `${environment.advContabilApi}/api/ContaContabil`;
  private readonly listarPlanoDeContas = `${this.apiUrl}/ListarContasContabeisPorNatureza`;
  private readonly listarContasContabeisPorNaturezaMaioresNiveisEFilhas = `${this.apiUrl}/ListarContasContabeisPorNaturezaMaioresNiveisEFilhas`;
  private readonly adicionarPlanoDeContas = `${this.apiUrl}/Inserir`;
  private readonly deletarPlanoDeContas = `${this.apiUrl}/Deletar/`;
  private readonly atualizarPlanoDeContas = `${this.apiUrl}/Atualizar/`;
  private readonly atualizarSaldoAnteriorCadastroPlanoDeContas = `${this.apiUrl}/AtualizarSaldoAnteriorCadastro/`;
  private readonly validaObrigatoriedadesPlanoDeContas = `${this.apiUrl}/ValidarObrigatoriedadesContaContabilSelecionada/`;

  constructor(
    private http: HttpClient,
    private cacheService: CacheService
  ) { }

  getContasContabeis(usuarioId: string): Observable<ContaContabil[]> {
    const params = new HttpParams().set('UsuarioId', usuarioId);
    return this.http.get<ContaContabil[]>(`${this.listarPlanoDeContas}`, { params });
  }

  listarRelatorioDRE(dataSelecionada: Date, usuarioId: string): Observable<Dre> {
    const params = new HttpParams()
      .set('dataSelecionada', dataSelecionada.toISOString())
      .set('usuarioId', usuarioId);

    return this.http.get<Dre>(`${this.apiUrl}/ListarRelatorioDRE`, { params });
  }

  getContasContabeisPorNaturezaMaioresNiveisEFilhas(userId: string): Observable<ContaContabil[]> {
    return this.cacheService.getOrSetContasContabeis(userId, () => {
      const params = new HttpParams().set('UsuarioId', userId);
      return this.http.get<ContaContabil[]>(`${this.listarContasContabeisPorNaturezaMaioresNiveisEFilhas}`, { params });
    });
  }

  getContasAnaliticasPorUsuario(userId: string): Observable<ContaContabil[]> {
    const params = new HttpParams().set('usuarioId', userId);
    return this.http.get<ContaContabil[]>(`${this.apiUrl}/ListarContasAnaliticasPorUsuario`, { params });
  }

  obterPlanoDeConta(planoDeContaId: any): Observable<ContaContabil> {
    return this.http.get<ContaContabil>(
      `${this.apiUrl}/obter/${planoDeContaId}`
    );
  }

  addContaContabil(contaContabil: any): Observable<any> {
    return this.http.post<any>(this.adicionarPlanoDeContas, contaContabil);
  }

  updatePlanoDeContas(contaContabil: any): Observable<any> {
    return this.http.put<any>(this.atualizarPlanoDeContas, contaContabil);
  }

  deletePlanoDeContas(id: any): Observable<any> {
    return this.http.delete<any>(`${this.deletarPlanoDeContas}${id}`).pipe(
      tap((resposta) => {
      }),
      catchError((error) => {
        console.error('Erro ao deletar conta contábil', error);
        return throwError(() => new Error('Falha ao deletar conta contábil'));
      })
    );
  }

  obterContasFilhasPorTipoBanco(tipoBanco: TipoBanco, usuarioId: string): Observable<any[]> {
    const params = new HttpParams()
      .set('tipoBanco', tipoBanco.toString())
      .set('usuarioId', usuarioId);

    return this.http.get<any[]>(`${this.apiUrl}/ObterContasFilhasPorTipoBanco`, { params });
  }

  validarObrigatoriedadesContaContabilSelecionada(id: string, usuarioId: string): Observable<any> {
    const params = new HttpParams().set('usuarioId', usuarioId);

    return this.http.get(`${this.validaObrigatoriedadesPlanoDeContas}${id}`, { params }).pipe(
      tap((resposta) => { }),
      catchError((error) => {
        console.error('Erro validar obrigatoriedade', error);
        return throwError(() => new Error('Erro validar obrigatoriedade'));
      })
    );
  }

  atualizarSaldoAnteriorCadastro(contaContabil: any, usuarioId: string): Observable<any> {
    const url = `${this.atualizarSaldoAnteriorCadastroPlanoDeContas}?usuarioId=${usuarioId}`;
    return this.http.put<any>(url, contaContabil);
  }

  listarRelatorioBalancete(dataInicio: Date, dataFim: Date, usuarioId: string): Observable<Balancete[]> {
    const params = new HttpParams()
      .set('dataInicio', dataInicio.toISOString())
      .set('dataFim', dataFim.toISOString())
      .set('usuarioId', usuarioId);

    return this.http.get<Balancete[]>(`${this.apiUrl}/ListarRelatorioBalancete`, { params }).pipe(
      retry(1),
      catchError(() => {
        return throwError(() => new Error('Erro ao listar relatório balancete'));
      })
    );
  }

  listarRelatorioBalancoPatrimonial(dataSelecionada: Date, usuarioId: string): Observable<any> {
    const params = new HttpParams()
      .set('dataSelecionada', dataSelecionada.toISOString())
      .set('usuarioId', usuarioId);

    return this.http.get<any>(`${this.apiUrl}/ListarRelatorioBalancoPatrimonial`, { params }).pipe(
      retry(1),
      catchError(() => {
        return throwError(() => new Error('Erro ao listar relatório de balanço patrimonial'));
      })
    );
  }

  listarRelatorioRazao(dataInicio: Date, dataFim: Date, usuarioId: string): Observable<TransacaoContabil[]> {
    const params = new HttpParams()
      .set('dataInicio', dataInicio.toISOString())
      .set('dataFim', dataFim.toISOString())
      .set('usuarioId', usuarioId);

    return this.http.get<TransacaoContabil[]>(`${this.apiUrl}/ListarRelatorioRazao`, { params });
  }
  invalidarCacheUsuario(userId: string): void {
    this.cacheService.delete(`contas_contabeis_${userId}`);
  }

  limparCache(): void {
    this.cacheService.invalidatePattern('contas_contabeis_');
  }
}
